import React, { useContext } from 'react';
import { enrollmentContext } from '../../../../hoc/context/enrollment/enrollment-context';
import context from '../../../../hoc/context/product/context';
import { Control, Controller, UseFormMethods } from 'react-hook-form';
import {
	formatDate,
	formatDateInput,
	unMaskValue,
} from '../../../../hoc/components/inputs/common-functions';
import {
	TextBox,
	FormUpdater,
	RadioGroup,
} from '../../../../hoc/components/inputs';
import { sendGaFocusEvent } from '../../patient/enrollment-ga-events';

export const PatientInformation: React.FC<{
	hookProps: {
		errors: UseFormMethods['errors'];
		register: UseFormMethods['register'];
		formState: UseFormMethods['formState'];
	};
	suffix?: string;
	control: Control;
	setValue: UseFormMethods['setValue'];
	clearErrors: UseFormMethods['clearErrors'];
}> = ({ hookProps, control, setValue, suffix = '', clearErrors }) => {
	const ctxt = useContext(context);
	const enrollmentCtxt = useContext(enrollmentContext);
	const firstNameMaxLength = 50;
	const lastNameMaxLength = 50;
	const dobMaxLength = 10;
	const dobMinLength = 8;
	return (
		<>
			<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
				<div className="col-span-1">
					<TextBox
						label={ctxt.json.getField('firstname' + suffix)?.label}
						name={'firstname' + suffix}
						help={ctxt.json.getField('firstname' + suffix)?.help}
						maxLength={firstNameMaxLength}
						{...hookProps}
						setValue={setValue}
						onChange={() => clearErrors('firstname' + suffix)}
					/>
					<FormUpdater control={control} controlName={'firstname' + suffix} />
				</div>
				<div className="col-span-1">
					<TextBox
						label={ctxt.json.getField('lastname' + suffix)?.label}
						name={'lastname' + suffix}
						help={ctxt.json.getField('lastname' + suffix)?.help}
						maxLength={lastNameMaxLength}
						{...hookProps}
						setValue={setValue}
						onChange={() => clearErrors('lastname' + suffix)}
					/>
					<FormUpdater control={control} controlName={'lastname' + suffix} />
				</div>
				{!suffix && (
					<div className="col-span-1">
						<Controller
							control={control}
							name={'dob'}
							render={({ onBlur, onChange, value }) => (
								<TextBox
									label={ctxt.json.getField('dob')?.label}
									name={'dob'}
									help={ctxt.json.getField('dob')?.help}
									maxLength={dobMaxLength}
									minLength={dobMinLength}
									format="##/##/####"
									placeholder={'MM/DD/YYYY'}
									mask={['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']}
									setValue={setValue}
									value={formatDate(value)}
									onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
										const dob = unMaskValue(
											change.target.value,
											['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y'],
											'##/##/####'
										);
										onChange(formatDateInput(dob));
										clearErrors('dob');
									}}
									onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
										//TODO: Use analytics props and remove onFocus
										sendGaFocusEvent(event.target.name);
									}}
									{...hookProps}
									onBlur={onBlur}
								/>
							)}
						/>
						<FormUpdater control={control} controlName={'dob'} />
					</div>
				)}
			</div>
			<div className="mt-12">
				<Controller
					control={control}
					name={'gender' + suffix}
					render={({ onChange, value }) => (
						<RadioGroup
							name={'gender' + suffix}
							label={ctxt.json.getField('gender' + suffix)?.label}
							help={ctxt.json.getField('gender' + suffix)?.help}
							ariaLabel={ctxt.json.getField('lastname' + suffix)?.label}
							defaultValue={
								enrollmentCtxt.getFormField('gender' + suffix)?.value
							}
							value={value}
							className="grid grid-cols-1 gap-5 md:grid-cols-2 mt-2"
							radioButtonClassName="col-span-1"
							options={ctxt.json.getField('gender' + suffix)?.options}
							onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
								//TODO: Use analytics props and remove onFocus
								sendGaFocusEvent(event.target.name);
							}}
							{...hookProps}
							onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
								onChange(change);
								clearErrors('gender' + suffix);
							}}
						/>
					)}
				/>
				<FormUpdater control={control} controlName={'gender' + suffix} />
			</div>
		</>
	);
};

export default PatientInformation;
